import type { AuthenticationDataDto } from '../types';

// ⚠️ These three key NAMES are a contract with the Playwright harness (e2e/session.ts patches them
// directly). Do not rename them without updating that file.
export const ACCESS_TOKEN_KEY = 'csc_wms_access_token';
export const REFRESH_TOKEN_KEY = 'csc_wms_refresh_token';
export const TOKEN_EXPIRY_KEY = 'csc_wms_token_expiry';

/**
 * 86d3zp17w — the POSITIVE signal that a session ended on purpose.
 *
 * Cross-tab logout used to be inferred from ACCESS_TOKEN_KEY disappearing, which cannot tell a
 * deliberate sign-out from a tab that merely gave up on a refresh. That is what let one tab losing a
 * rotation race drag every other tab — including tabs holding a freshly-valid token — to /login.
 * Only an explicit sign-out writes this. See useLogoutSync.
 */
export const SESSION_END_KEY = 'csc_wms_session_end';

/**
 * In-tab counterpart to the storage event. A `storage` event fires only in OTHER tabs, so the tab
 * that ends the session needs its own signal to react to. Listened for by useLogoutSync, which
 * navigates via React Router — replacing the `window.location.href = '/login'` hard navigation that
 * discarded unsaved work and forced a cold app boot.
 */
export const SESSION_END_EVENT = 'csc:session-end';

export type SessionEndReason = 'user-logout' | 'expired' | 'revoked';

export interface SessionEndMarker {
  reason: SessionEndReason;
  at: number;
}

// Vitest runs in the `node` environment (vite.config.ts sets no `environment`, and there is no jsdom),
// so `localStorage` is simply absent under test. Same guard shape as DetailPageLayout.tsx:34.
function storage(): Storage | null {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
    ? window.localStorage
    : null;
}

function read(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    // Hardened profiles and private windows THROW on access rather than returning null.
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    storage()?.setItem(key, value);
  } catch {
    /* no-op — see read() */
  }
}

function remove(key: string): void {
  try {
    storage()?.removeItem(key);
  } catch {
    /* no-op — see read() */
  }
}

class TokenService {
  getAccessToken(): string | null {
    return read(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return read(REFRESH_TOKEN_KEY);
  }

  setTokens(authData: AuthenticationDataDto): void {
    write(ACCESS_TOKEN_KEY, authData.accessToken);
    write(REFRESH_TOKEN_KEY, authData.refreshToken);
    write(TOKEN_EXPIRY_KEY, authData.accessTokenExpiration);
    // A successful sign-in or refresh clears any stale end-marker, so a later `storage` event from a
    // previous session cannot eject the tab that just authenticated.
    remove(SESSION_END_KEY);
  }

  /**
   * Drop this tab's copy of the tokens. **Purely local — carries no cross-tab meaning.**
   *
   * ⚠️ Do NOT use this to end a session. Removing the shared access-token key is exactly what used to
   * broadcast one tab's recoverable failure to every other tab. Call {@link endSession} instead when
   * the session is genuinely over.
   */
  clearTokens(): void {
    remove(ACCESS_TOKEN_KEY);
    remove(REFRESH_TOKEN_KEY);
    remove(TOKEN_EXPIRY_KEY);
  }

  /** End the session deliberately: write the marker FIRST, then clear, so siblings learn the reason. */
  endSession(reason: SessionEndReason): void {
    write(SESSION_END_KEY, JSON.stringify({ reason, at: Date.now() } satisfies SessionEndMarker));
    this.clearTokens();
  }

  readSessionEnd(): SessionEndMarker | null {
    const raw = read(SESSION_END_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as SessionEndMarker;
    } catch {
      return null;
    }
  }

  clearSessionEnd(): void {
    remove(SESSION_END_KEY);
  }

  isTokenExpired(): boolean {
    const expiry = read(TOKEN_EXPIRY_KEY);
    if (!expiry) return true;

    const expiryDate = new Date(expiry);
    // Consider expired if less than 15 seconds remaining
    return expiryDate.getTime() - Date.now() < 15000;
  }

  shouldRefreshToken(): boolean {
    const expiry = read(TOKEN_EXPIRY_KEY);
    if (!expiry) return false;

    const expiryDate = new Date(expiry);
    // Refresh when less than 1 minute remaining
    return expiryDate.getTime() - Date.now() < 60000;
  }

  getTokenExpiry(): Date | null {
    const expiry = read(TOKEN_EXPIRY_KEY);
    return expiry ? new Date(expiry) : null;
  }
}

export const tokenService = new TokenService();
